import React, { useState, useMemo } from 'react';
import HrnbButton from '../controls/HrnbButton';

const WEEKDAYS = ['Пн', 'Вт', 'Ср', 'Чт', 'Пт', 'Сб', 'Вс'];
const MONTH_NAMES = [
    'Январь', 'Февраль', 'Март', 'Апрель', 'Май', 'Июнь',
    'Июль', 'Август', 'Сентябрь', 'Октябрь', 'Ноябрь', 'Декабрь'
];

function getDaysInMonth(year, month) {
    return new Date(year, month + 1, 0).getDate();
}

function getFirstDayOfMonth(year, month) {
    const day = new Date(year, month, 1).getDay();
    return day === 0 ? 6 : day - 1; // Пн=0, Вс=6
}

function isSameDay(d1, d2) {
    return (
        d1.getFullYear() === d2.getFullYear() &&
        d1.getMonth() === d2.getMonth() &&
        d1.getDate() === d2.getDate()
    );
}

function parseEventDate(isoString) {
    if (!isoString) return null;
    const d = new Date(isoString);
    return isNaN(d.getTime()) ? null : d;
}

export default function EventCalendar({ events = [], onSelectDay, onSelectEvent }) {
    const [currentDate, setCurrentDate] = useState(new Date());
    const [selectedDay, setSelectedDay] = useState(null);

    const year = currentDate.getFullYear();
    const month = currentDate.getMonth();

    const daysInMonth = getDaysInMonth(year, month);
    const firstDayIndex = getFirstDayOfMonth(year, month);

    const eventsByDay = useMemo(() => {
        const map = new Map();
        events.forEach((event) => {
            const date = parseEventDate(event.startTime);
            if (!date) return;
            const key = `${date.getFullYear()}-${date.getMonth()}-${date.getDate()}`;
            if (!map.has(key)) map.set(key, []);
            map.get(key).push(event);
        });
        return map;
    }, [events]);

    const today = new Date();

    const handlePrevMonth = () => {
        setCurrentDate(new Date(year, month - 1, 1));
        setSelectedDay(null);
    };

    const handleNextMonth = () => {
        setCurrentDate(new Date(year, month + 1, 1));
        setSelectedDay(null);
    };

    const handleDayClick = (day) => {
        const date = new Date(year, month, day);
        setSelectedDay(date);
        if (onSelectDay) onSelectDay(date);
    };

    const renderDays = () => {
        const days = [];
        // Пустые ячейки до начала месяца
        for (let i = 0; i < firstDayIndex; i++) {
            days.push(<div key={`empty-${i}`} className="event-calendar-day event-calendar-day--empty" />);
        }
        // Дни месяца
        for (let day = 1; day <= daysInMonth; day++) {
            const date = new Date(year, month, day);
            const key = `${date.getFullYear()}-${date.getMonth()}-${date.getDate()}`;
            const dayEvents = eventsByDay.get(key) || [];
            const isToday = isSameDay(date, today);
            const isSelected = selectedDay && isSameDay(date, selectedDay);

            days.push(
                <button
                    key={day}
                    className={`event-calendar-day ${isToday ? 'event-calendar-day--today' : ''} ${isSelected ? 'event-calendar-day--selected' : ''} ${dayEvents.length > 0 ? 'event-calendar-day--has-events' : ''}`}
                    onClick={() => handleDayClick(day)}
                    type="button"
                >
                    <span className="event-calendar-day-number">{day}</span>
                    {dayEvents.length > 0 && (
                        <div className="event-calendar-day-dots">
                            {dayEvents.slice(0, 3).map((evt, idx) => (
                                <span
                                    key={idx}
                                    className={`event-calendar-dot event-calendar-dot--${(evt.eventType || 'CUSTOM').toLowerCase()}`}
                                    title={evt.title}
                                />
                            ))}
                            {dayEvents.length > 3 && (
                                <span className="event-calendar-dot event-calendar-dot--more">+</span>
                            )}
                        </div>
                    )}
                </button>
            );
        }
        return days;
    };

    const selectedDayKey = selectedDay
        ? `${selectedDay.getFullYear()}-${selectedDay.getMonth()}-${selectedDay.getDate()}`
        : null;
    const selectedDayEvents = selectedDayKey ? (eventsByDay.get(selectedDayKey) || []) : [];

    return (
        <div className="event-calendar">
            <div className="event-calendar-header">
                <HrnbButton variant="ghost" onClick={handlePrevMonth}>‹</HrnbButton>
                <h3 className="event-calendar-month">
                    {MONTH_NAMES[month]} {year}
                </h3>
                <HrnbButton variant="ghost" onClick={handleNextMonth}>›</HrnbButton>
            </div>

            <div className="event-calendar-grid">
                {WEEKDAYS.map((wd) => (
                    <div key={wd} className="event-calendar-weekday">{wd}</div>
                ))}
                {renderDays()}
            </div>

            {selectedDay && selectedDayEvents.length > 0 && (
                <div className="event-calendar-selected-day">
                    <h4 className="event-calendar-selected-title">
                        События {selectedDay.toLocaleDateString('ru-RU')}
                    </h4>
                    <div className="event-calendar-selected-list">
                        {selectedDayEvents.map((evt) => (
                            <button
                                key={evt.id}
                                className="event-calendar-mini-card"
                                onClick={() => onSelectEvent && onSelectEvent(evt)}
                                type="button"
                            >
                                <span className={`event-calendar-mini-type event-calendar-mini-type--${(evt.eventType || 'CUSTOM').toLowerCase()}`}>
                                    {getEventTypeLabel(evt.eventType)}
                                </span>
                                <span className="event-calendar-mini-title">{evt.title}</span>
                                <span className="event-calendar-mini-time">
                                    {formatTime(evt.startTime)}
                                    {evt.endTime ? ` — ${formatTime(evt.endTime)}` : ''}
                                </span>
                            </button>
                        ))}
                    </div>
                </div>
            )}
        </div>
    );
}

function getEventTypeLabel(type) {
    const labels = {
        BATTLE_START: 'Баттл',
        ROUND_START: 'Раунд',
        ROUND_END: 'Раунд',
        VOTING_START: 'Голосование',
        VOTING_END: 'Голосование',
        REGISTRATION_DEADLINE: 'Регистрация',
        TRACK_UPLOAD_DEADLINE: 'Загрузка',
        LIVE_STREAM: 'Стрим',
        MEETING: 'Встреча',
        CUSTOM: 'Другое',
    };
    return labels[type] || type || 'Другое';
}

function formatTime(isoString) {
    if (!isoString) return '';
    const d = new Date(isoString);
    if (isNaN(d.getTime())) return '';
    return d.toLocaleTimeString('ru-RU', { hour: '2-digit', minute: '2-digit' });
}
